/**
 * @file OrderKanbanCard.tsx
 * @description Карточка заказа на operational Kanban-доске (phase10.5 шаг 2).
 *
 * Паттерн зеркалит CRM `KanbanCard.tsx`: useDraggable + DragOverlay-режим.
 * Показывает: номер, клиента, сумму, кол-во позиций, источник и стикеры
 * «чей ход» / «нужно решение».
 */

import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Package, User } from "lucide-react";

import { OrderAgreementBadge } from "./OrderAgreementBadge";
import { OrderRowChips } from "./OrderRowChips";
import { Badge } from "../ui/badge";
import { formatMoney, type OrderDto } from "../../lib/api-orders";
import { safeStageColor } from "../../lib/stage-color";

interface Props {
  order: OrderDto;
  /** Если true — рендер в DragOverlay (без useDraggable). */
  dragging?: boolean;
  onClick?: () => void;
}

export function OrderKanbanCard({ order, dragging, onClick }: Props) {
  const draggable = useDraggable({
    id: order.id,
    data: {
      type: "order",
      orderId: order.id,
      fromStageId: order.stage?.id ?? null,
    },
    disabled: dragging,
  });

  // Цвет магазина уходит в `style` — только проверенный hex.
  const stageColor = safeStageColor(order.stage?.color);
  const style: React.CSSProperties = dragging
    ? {
        cursor: "grabbing",
        ...(stageColor ? { borderLeft: `4px solid ${stageColor}` } : {}),
      }
    : {
        transform: CSS.Translate.toString(draggable.transform),
        opacity: draggable.isDragging ? 0 : 1,
        ...(stageColor ? { borderLeft: `4px solid ${stageColor}` } : {}),
      };

  return (
    <div
      ref={dragging ? undefined : draggable.setNodeRef}
      style={style}
      {...(dragging ? {} : draggable.attributes)}
      {...(dragging ? {} : draggable.listeners)}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
      className={`overflow-hidden rounded-lg bg-surface-lowest p-3 shadow-sm cursor-grab active:cursor-grabbing select-none transition-shadow hover:shadow ${
        dragging ? "rotate-2 shadow-lg" : ""
      }`}
    >
      <div className="flex items-center gap-2">
        <Package className="size-3.5 text-on-surface-variant shrink-0" />
        {/* Номер — обычным шрифтом с табличными цифрами (SC43), первой линией
            во всю ширину: чип хода рядом резал его до «Z-2026-0000…» (qa4 п. 2). */}
        <span className="type-body truncate font-medium tabular-nums" title={order.number}>
          {order.number}
        </span>
      </div>
      <div className="mt-1 flex min-w-0 items-center gap-2">
        {order.customerDisplayName ? (
          <span className="type-caption flex min-w-0 items-center gap-1 text-on-surface-variant">
            <User className="size-3 shrink-0" />
            <span className="truncate">{order.customerDisplayName}</span>
          </span>
        ) : null}
        {/* Стикеры «чей ход» и «нужно решение» — второй линией справа, та же
            система, что в реестре и в карточке заказа (phase19.24 §14). */}
        <OrderRowChips
          order={order}
          short
          className="ml-auto flex shrink-0 items-center gap-1"
        />
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <span className="type-body font-semibold tabular-nums text-on-surface">
          {formatMoney(order.totalGross)} ₽
        </span>
        <div className="flex items-center gap-1 flex-wrap">
          {/* Чего ждёт заявка «по договорённости» — видно, не открывая заказ. */}
          <OrderAgreementBadge status={order.deliveryAgreement?.status} />
          <Badge variant="outline" className="font-normal">
            {order.itemsCount} поз.
          </Badge>
        </div>
      </div>
      {order.ownerDisplayName ? (
        <div className="type-caption mt-1 truncate text-on-surface-variant">
          {order.ownerDisplayName}
        </div>
      ) : null}
    </div>
  );
}
